import { Range } from "react-range";
import React, { useEffect, useState } from "react";

interface ColFormValueSliderProps {
  title: string;
  step: number;
  value: number | number[];
  leftColumnValue?: number | number[];
  leftColumnLabel?: string;
  leftColumnFormatFunction?: (value: number | number[]) => string;
  rightColumnValue?: number | number[];
  rightColumnLabel?: string;
  rightColumnFormatFunction?: (value: number | number[]) => string;
  minAmount: number;
  maxAmount: number;
  onChange?: (amount: number) => void;
  onFinalChange?: (amount: number) => void;
}

const ColFormValueSlider = (props: ColFormValueSliderProps) => {
  const {
    step,
    title,
    value,
    leftColumnValue,
    leftColumnLabel,
    leftColumnFormatFunction,
    rightColumnValue,
    rightColumnLabel,
    rightColumnFormatFunction,
    minAmount,
    maxAmount,
    onChange,
    onFinalChange,
  } = props;

  const hasLeft = typeof leftColumnValue === "number";
  const hasRight = typeof rightColumnValue === "number";

  const [rangeValues, setRangeValues] = useState(Array.isArray(value) ? value : [value]);

  useEffect(() => {
    setRangeValues(Array.isArray(value) ? value : [value]);
  }, [value]);

  const _onChange = (amount: number[]) => {
    setRangeValues(amount);
    if (!onChange) return;
    onChange(amount[0]);
  };

  const _onFinalChange = (amount: number[]) => {
    if (!onFinalChange) return;
    onFinalChange(amount[0]);
  };

  const percentage = ((rangeValues[0] - minAmount) / (maxAmount - minAmount)) * 100;

  return (
    <div className="space-y-3">
      <h6 className="text-sm font-semibold text-gray-700">{title}</h6>

      {(hasLeft || hasRight) && (
        <div className="flex items-end justify-between gap-4">
          {hasLeft && (
            <div className="text-center">
              {leftColumnLabel && <p className="text-xs text-gray-500 mb-1">{leftColumnLabel}</p>}
              <p className="text-lg font-bold text-gray-900">
                {leftColumnFormatFunction ? leftColumnFormatFunction(leftColumnValue) : leftColumnValue}
              </p>
            </div>
          )}
          {hasRight && (
            <div className="text-center">
              {rightColumnLabel && <p className="text-xs text-gray-500 mb-1">{rightColumnLabel}</p>}
              <p className="text-lg font-bold text-gray-900">
                {rightColumnFormatFunction ? rightColumnFormatFunction(rightColumnValue) : rightColumnValue}
              </p>
            </div>
          )}
        </div>
      )}

      <Range
        step={step}
        min={minAmount}
        max={maxAmount}
        values={rangeValues}
        onChange={_onChange}
        onFinalChange={_onFinalChange}
        renderTrack={({ props, children }) => (
          <div {...props} className="h-2 w-full rounded-full bg-gray-200 relative cursor-pointer" style={props.style}>
            <div
              className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-[#9BFBE0] to-[#0FD131]"
              style={{ width: `${percentage}%` }}
            />
            {children}
          </div>
        )}
        renderThumb={({ props }) => {
          const { key, ...thumbProps } = props;
          return (
            <div
              key={key}
              {...thumbProps}
              className="size-5 rounded-full bg-white border-2 border-[#0FD131] shadow-md focus:outline-none focus:ring-2 focus:ring-[#9BFBE0] cursor-grab active:cursor-grabbing"
              style={props.style}
            />
          );
        }}
      />
    </div>
  );
};

export default ColFormValueSlider;
